import { useState, useEffect } from 'react';

export interface UsePersistentStateOptions<T> {
  /** Custom serializer for complex types (e.g., Date objects) */
  serialize?: (value: T) => string;
  /** Custom deserializer for complex types */
  deserialize?: (stored: string) => T;
}

/**
 * A hook that persists state to localStorage.
 * State is automatically loaded from localStorage on mount and saved whenever it changes.
 *
 * @param key - Unique localStorage key (should be namespaced, e.g., 'filters:dataReview:checks')
 * @param defaultValue - Default value if no stored value exists
 * @param options - Optional custom serialize/deserialize functions for complex types
 * @returns Tuple of [state, setState] like useState
 */
export function usePersistentState<T>(
  key: string,
  defaultValue: T,
  options?: UsePersistentStateOptions<T>
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const { serialize = JSON.stringify, deserialize = JSON.parse } = options || {};

  const [state, setState] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) return defaultValue;
      return deserialize(stored);
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, serialize(state));
    } catch (e) {
      // Handle quota exceeded or other storage errors silently
      console.warn(`Failed to persist state for key "${key}":`, e);
    }
  }, [key, state, serialize]);

  return [state, setState];
}
